import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { LuChevronLeft, LuChevronRight, LuNotebookPen } from "react-icons/lu";
import { COURSES } from "@/lib/data/courses";
import { useAuthStore } from "@/lib/stores/authStore";
import { useCourseNotesStore } from "@/lib/stores/courseNotesStore";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";
import { useLocale } from "@/contexts/IntlContext";
import { pickLocalized } from "@/lib/utils/localized";
import { ProfileCard } from "@/components/ProfileCard";
import { ProfileHeader } from "@/components/ProfileHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/trainer/notes/")({
  component: NotesHome,
  head: () => ({ meta: [{ title: "My notes - HAMD" }] }),
});

function NotesHome() {
  const user = useAuthStore((s) => s.user);
  const isTrainer = user?.accountType === "TRAINER";
  const { locale } = useLocale();
  const isRTL = locale === "ar";
  const { setAuthOrigin } = useTrainerAuthRedirect();

  const notesByCourse = useCourseNotesStore((s) => s.notesByCourse);
  const loadNotes = useCourseNotesStore((s) => s.loadNotes);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTrainer) {
      setLoading(false);
      return;
    }
    let alive = true;
    Promise.all(COURSES.map((c) => loadNotes(c.slug))).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isTrainer, loadNotes]);

  if (!isTrainer) {
    return (
      <div className="px-6 py-8 space-y-6">
        <ProfileHeader
          title={<FormattedMessage id="trainer.notes.title" />}
          subtitle={<FormattedMessage id="trainer.notes.subtitle" />}
        />
        <ProfileCard className="p-4 space-y-3">
          <p className="text-small text-dark-gray">
            <FormattedMessage id="trainer.notes.guestPrompt" />
          </p>
          <Link
            to="/trainer/auth"
            onClick={() => setAuthOrigin("/trainer/notes")}
            className="block"
          >
            <Button className="w-full">
              <FormattedMessage id="trainer.auth.registerOrSignIn" />
            </Button>
          </Link>
        </ProfileCard>
      </div>
    );
  }

  const counts = COURSES.map((course) => ({
    course,
    count: Object.values(notesByCourse[course.slug] ?? {}).filter((c) =>
      c.trim(),
    ).length,
  }));
  const total = counts.reduce((sum, c) => sum + c.count, 0);

  return (
    <main className="space-y-5 px-4 pb-6">
      <header className="space-y-1">
        <h1 className="text-h1 font-medium text-black">
          <FormattedMessage id="trainer.notes.title" />
        </h1>
        <p className="text-small text-dark-gray">
          <FormattedMessage id="trainer.notes.subtitle" />
        </p>
      </header>

      {loading ? (
        <p className="text-small text-dark-gray">
          <FormattedMessage id="trainer.notes.loading" />
        </p>
      ) : (
        <>
          {total === 0 && (
            <div className="rounded-2xl bg-secondary/40 p-4">
              <p className="text-small text-dark-gray">
                <FormattedMessage id="trainer.notes.empty" />
              </p>
            </div>
          )}

          <div className="space-y-3">
            {counts.map(({ course, count }) => (
              <Link
                key={course.slug}
                to="/trainer/notes/$courseSlug"
                params={{ courseSlug: course.slug }}
                className="block"
              >
                <div className="flex items-center gap-3 rounded-2xl bg-card p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary/50 text-primary">
                    <LuNotebookPen size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-small font-medium text-black">
                      {pickLocalized(course.title, locale)}
                    </p>
                    <p className="text-micro text-dark-gray">
                      <FormattedMessage
                        id="trainer.notes.count"
                        values={{ count }}
                      />
                    </p>
                  </div>
                  <span className="shrink-0 text-gray">
                    {isRTL ? (
                      <LuChevronLeft size={20} />
                    ) : (
                      <LuChevronRight size={20} />
                    )}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </main>
  );
}
